import { useCallback, useEffect, useRef, useState } from 'react'

interface IUseChatScrollParams<T> {
  items: T[]
  resetKey?: string | number | null
  isOwnItem: (item: T) => boolean
  bottomThreshold?: number
}

export const useChatScroll = <T, >({
  items,
  resetKey,
  isOwnItem,
  bottomThreshold = 48
}: IUseChatScrollParams<T>) => {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const isAtBottomRef = useRef(true)
  const previousItemsLengthRef = useRef(0)
  const itemsLengthRef = useRef(items.length)
  const [newItemsCount, setNewItemsCount] = useState(0)
  itemsLengthRef.current = items.length

  const resetScrollState = useCallback(() => {
    isAtBottomRef.current = true
    previousItemsLengthRef.current = 0
    setNewItemsCount(0)
  }, [])

  const scrollToLatestItem = useCallback((behavior: ScrollBehavior = 'smooth') => {
    const scroll = () => {
      const container = containerRef.current
      if (!container) return

      container.scrollTo({
        top: container.scrollHeight,
        behavior
      })
      isAtBottomRef.current = true
      setNewItemsCount(0)
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(scroll)
    })
  }, [])

  useEffect(() => {
    resetScrollState()
    if (itemsLengthRef.current) {
      scrollToLatestItem('auto')
    }
  }, [resetKey, resetScrollState, scrollToLatestItem])

  useEffect(() => {
    const previousItemsLength = previousItemsLengthRef.current
    previousItemsLengthRef.current = items.length

    if (!items.length) {
      setNewItemsCount(0)
      return
    }

    const addedItemsCount = items.length - previousItemsLength
    const lastItem = items[items.length - 1]
    const shouldScrollToBottom = previousItemsLength === 0 || isAtBottomRef.current || isOwnItem(lastItem)

    if (shouldScrollToBottom) {
      scrollToLatestItem(previousItemsLength === 0 ? 'auto' : 'smooth')
      return
    }

    if (addedItemsCount > 0) {
      setNewItemsCount((count) => count + addedItemsCount)
    }
  }, [items, isOwnItem, scrollToLatestItem])

  const handleScroll = useCallback(() => {
    const container = containerRef.current
    if (!container) return

    const distanceFromBottom = container.scrollHeight - container.clientHeight - container.scrollTop
    const isAtBottom = distanceFromBottom <= bottomThreshold

    isAtBottomRef.current = isAtBottom
    if (isAtBottom) {
      setNewItemsCount(0)
    }
  }, [bottomThreshold])

  return {
    containerRef,
    newItemsCount,
    handleScroll,
    resetScrollState,
    scrollToLatestItem
  }
}
